// Кнопка «проверить цены». Стоит и в шапке варианта (с подписью и числом
// рейсов), и в строке действий карточки (compact — одна иконка).
//
// Гостю кнопка видна и нажимается: демо-поездка на витрине обязана показывать,
// что продукт умеет проверять цены. Запроса при этом не происходит — нажатие
// зовёт вход. Это тот же приём, что у снимков, шаринга и лишнего варианта:
// живая кнопка, ведущая ко входу, честнее мёртвой. Именно живая, а не
// `disabled`: до отключённой не добраться с клавиатуры, и она не объясняет
// ничего.
//
// Кнопки нет вовсе, когда фичи нет или проверять нечего.
import { useEffect, useId, useMemo, useState, type ReactElement } from 'react';
import type { Segment } from '../types';
import { useAuth } from '../auth';
import { useT } from '../i18n';
import { usePrices } from './PricesProvider';
import { limitFlights } from './staleness';
import { isFullConfig } from './types';
import styles from './PriceRefreshButton.module.css';

export interface PriceRefreshButtonProps {
  segments: readonly Segment[];
  /** Только иконка — для строки действий карточки. */
  compact?: boolean;
  className?: string;
}

function RefreshIcon(): ReactElement {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M20 12a8 8 0 1 1-2.34-5.66" />
      <path d="M20 4v5h-5" />
    </svg>
  );
}

/**
 * Чем стать живой области: строкой в потоке, всплывающим слоем или ничем
 * видимым. Скринридеру она читается в любом случае — речь только о глазах.
 */
function statusClass(
  compact: boolean,
  busy: boolean,
  askedToSignIn: boolean,
  error: string | null,
): string {
  if (!compact) {
    return busy || askedToSignIn || error !== null ? styles.status : 'visually-hidden';
  }
  // У иконки показываем только ошибку: она единственная ничем другим себя не
  // выдаёт. Работу видно по иконке, ответ гостю — по окну входа.
  return error !== null && !busy ? styles.toast : 'visually-hidden';
}

export function PriceRefreshButton({
  segments,
  compact,
  className,
}: PriceRefreshButtonProps): ReactElement | null {
  const { visible, ready, config, refresh, busy, errorFor } = usePrices();
  const { requestSignIn } = useAuth();
  const t = useT();

  // Своя метка на всю жизнь компонента. Ошибка живёт в контексте, одна на
  // модуль, а кнопок на доске десятки — показать сообщение должна ровно та,
  // что затеяла неудавшуюся проверку, иначе одна неудача даёт два десятка
  // одинаковых всплывашек и столько же объявлений для скринридера.
  const ownerId = useId();
  const error = errorFor(ownerId);

  const flights = useMemo(() => segments.filter((s) => s.kind === 'flight'), [segments]);
  const busyNow = flights.some((f) => busy(f.id));

  // Гость нажал, потом вошёл — объяснение своё дело сделало и должно уйти.
  const [askedToSignIn, setAskedToSignIn] = useState(false);
  useEffect(() => {
    if (ready) setAskedToSignIn(false);
  }, [ready]);

  if (!visible || flights.length === 0) return null;

  // Фича видна, но запускать её этому человеку нельзя — значит он гость.
  const guest = !ready;
  const guestNote = t('prices.guest');

  // Число в подписи — сколько рейсов реально уйдёт на проверку: предел сервера
  // соблюдаем и здесь, обещать больше, чем проверим, нельзя. Гость предела не
  // знает (в его ответе этого поля нет) и видит полное число — так честнее,
  // чем показать ему выдуманный потолок.
  const count = limitFlights(flights, isFullConfig(config) ? config.maxFlights : undefined)
    .sent.length;
  const label = count > 1 ? t('prices.action.refreshMany', { count }) : t('prices.action.refresh');
  // Условие видно до нажатия, а не только после: подсказка на кнопке и есть
  // то место, где человек ищет объяснение, прежде чем ткнуть.
  // Ошибка у compact-кнопки уходит туда же: строка ошибки под каждой карточкой
  // повторила бы одно и то же сообщение столько раз, сколько рейсов.
  const title = guest ? `${label} — ${guestNote}` : busyNow ? t('prices.busy') : (error ?? label);

  return (
    <span className={styles.wrap}>
      <button
        type="button"
        className={[
          'btn',
          'btn-sm',
          compact ? styles.iconBtn : styles.btn,
          className ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => {
          if (guest) {
            // Ни одного запроса: у гостя поездка своя, локальная, и проверять
            // её на сервере нечем. Вместо отказа — путь к тому, чтобы стало
            // можно.
            setAskedToSignIn(true);
            requestSignIn();
            return;
          }
          void refresh(flights, ownerId);
        }}
        disabled={busyNow}
        aria-busy={busyNow || undefined}
        // Незрячему имя кнопки — единственный источник условия: подсказка
        // мышью ему недоступна, поэтому объяснение уходит в имя целиком.
        aria-label={guest ? `${label}. ${guestNote}` : compact ? label : undefined}
        title={title}
      >
        <span className={busyNow ? styles.spinning : undefined}>
          <RefreshIcon />
        </span>
        {!compact && <span className={styles.label}>{label}</span>}
      </button>

      {/* Проверка идёт секундами и молча — без этого зрячий видит хотя бы
          кручение иконки, а незрячий не узнаёт вообще ничего. Сюда же уходит
          ответ гостю на нажатие: окно входа открывается поверх страницы, но
          причина, по которой оно открылось, должна прозвучать здесь.

          У кнопки-иконки строке в потоке места нет — ни в ряду действий
          карточки, ни в шапке варианта на телефоне. Но молчать она не может:
          `title` пальцем не достать, и ошибка проверки осталась бы невидимой
          совсем. Поэтому ошибка (в том числе предупреждение об обрезанном
          списке) всплывает отдельным слоем поверх вёрстки, не занимая места.

          Работа и ответ гостю в этом режиме слоя не требуют: о первой говорит
          крутящаяся иконка, о втором — открывшееся окно входа. */}
      <span
        role="status"
        aria-live="polite"
        className={statusClass(compact === true, busyNow, askedToSignIn, error)}
      >
        {busyNow ? t('prices.busy') : askedToSignIn ? guestNote : (error ?? '')}
      </span>
    </span>
  );
}
